import React, { useState } from 'react';
import styled from 'styled-components';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import {
  CollapsedRail,
  CollapsedRailItem,
  CollapsedRailSection,
  WorkspaceRailToggle,
} from '../shared/Workspace';

// Import ticket screens for retail asset classes
import EquityTicketScreen from '../tickets/EquityTicketScreen';
import FixedIncomeTicketScreen from '../tickets/FixedIncomeTicketScreen';
import FXTicketScreen from '../tickets/FXTicketScreen';

/**
 * RetailTradeEntry - Trade entry screen optimized for RIA/retail clients.
 *
 * Features:
 * - Household filter (filter accounts by household/family)
 * - Account selector (portfolios filtered by household)
 * - Model Portfolio assignment and drift indicator
 * - Tax-aware warnings (wash sale, tax lot selection)
 * - Simplified asset class selection
 * - No Asset Manager filter (RIA is the only manager)
 * - Advisor instead of Portfolio Manager terminology
 *
 * This component wraps the asset-class specific ticket screens and provides
 * retail-specific field rendering and context.
 */

const Container = styled.div`
  min-height: calc(100vh - 64px);
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  padding: 1.5rem 2rem;
  background: rgba(30, 41, 59, 0.6);
  border-bottom: 1px solid rgba(148, 163, 184, 0.1);
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Subtitle = styled.p`
  color: #94a3b8;
  margin: 0;
  font-size: 0.85rem;
`;

const PageLayout = styled.div`
  display: flex;
  flex: 1;
`;

const Sidebar = styled.div<{ $collapsed?: boolean }>`
  width: ${p => p.$collapsed ? '64px' : '260px'};
  min-width: ${p => p.$collapsed ? '64px' : '260px'};
  background: rgba(15, 23, 42, 0.6);
  border-right: 1px solid rgba(148, 163, 184, 0.1);
  padding: ${p => p.$collapsed ? 0 : '2rem 1rem'};
  display: flex;
  flex-direction: column;
  transition: width 0.18s ease, min-width 0.18s ease;
`;

const SidebarHeading = styled.div`
  font-size: 0.7rem;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  padding: 0 1rem;
  margin-bottom: 1rem;
`;

const SidebarNav = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const SidebarTab = styled.button<{ active?: boolean }>`
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-weight: 500;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  border: none;
  background: ${props => props.active ? 'rgba(16, 185, 129, 0.1)' : 'transparent'};
  color: ${props => props.active ? '#34d399' : '#94a3b8'};
  position: relative;
  text-align: left;

  ${props => props.active && `
    &::before {
      content: '';
      position: absolute;
      left: -1rem;
      top: 50%;
      transform: translateY(-50%);
      height: 60%;
      width: 4px;
      background: #10b981;
      border-radius: 0 4px 4px 0;
    }
  `}

  &:hover {
    background: ${props => props.active ? 'rgba(16, 185, 129, 0.15)' : 'rgba(255, 255, 255, 0.05)'};
    color: ${props => props.active ? '#34d399' : '#e2e8f0'};
  }
`;

const ContentArea = styled.div`
  flex: 1;
  padding: 2rem;
  overflow-y: auto;
`;

const ContentInner = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

// Simplified asset classes for retail (less complex than institutional)
type MenuItem = {
  path?: string;
  label: string;
  icon?: string;
  subItems?: { path: string; label: string; icon?: string }[];
};

type FlatMenuItem = {
  path: string;
  label: string;
  icon?: string;
};

const menuItems: MenuItem[] = [
  { path: 'equity', label: 'Equity', icon: '📈' },
  { path: 'fixed-income', label: 'Fixed Income', icon: '📊' },
  {
    label: 'FX',
    icon: '💱',
    subItems: [
      { path: 'fx/spot', label: 'Spot', icon: '💱' },
      { path: 'fx/forward', label: 'Forward', icon: '📅' },
      { path: 'fx/swap', label: 'FX Swap', icon: '🔄' },
      { path: 'fx/ndf', label: 'NDF', icon: '📊' },
    ]
  },
];

const RetailTradeEntry: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Determine current asset class from path
  const currentPath = location.pathname.replace('/trade-entry/', '').replace('/trade-entry', '');

  // Auto-expand menus if active
  const isFxActive = currentPath.startsWith('fx/');
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>({
    FX: isFxActive,
  });

  const toggleMenu = (label: string) => {
    setExpandedMenus(prev => ({
      ...prev,
      [label]: !prev[label]
    }));
  };

  const handleAssetClassClick = (path: string) => {
    navigate(`/trade-entry/${path}`);
  };

  const collapsedSections = menuItems.map(item => {
    const items: FlatMenuItem[] = item.subItems
      ? item.subItems
      : item.path
        ? [{ path: item.path, label: item.label, icon: item.icon }]
        : [];
    return { label: item.label, items };
  }).filter(section => section.items.length > 0);

  return (
    <Container>
      <Header>
        <HeaderContent>
          <HeaderLeft>
            <Title>Trade Entry</Title>
            <Subtitle>
              Client account trading with tax efficiency and model portfolio adherence
            </Subtitle>
          </HeaderLeft>
        </HeaderContent>
      </Header>

      <PageLayout>
        <Sidebar $collapsed={sidebarCollapsed}>
          <WorkspaceRailToggle
            collapsed={sidebarCollapsed}
            label="Trade Entry"
            onToggle={() => setSidebarCollapsed(prev => !prev)}
          />
          {sidebarCollapsed ? (
            <CollapsedRail>
              {collapsedSections.map(section => (
                <CollapsedRailSection key={section.label}>
                  {section.items.map(item => (
                    <CollapsedRailItem
                      key={item.path}
                      $active={currentPath.startsWith(item.path)}
                      $tone="emerald"
                      aria-label={item.label}
                      title={item.label}
                      onClick={() => handleAssetClassClick(item.path)}
                    >
                      <span>{item.icon}</span>
                    </CollapsedRailItem>
                  ))}
                </CollapsedRailSection>
              ))}
            </CollapsedRail>
          ) : (
            <>
              <SidebarHeading>TRADE ENTRY</SidebarHeading>
              <SidebarNav>
                {menuItems.map(item => {
                  if (item.subItems) {
                    const isActive = item.subItems.some(sub => currentPath.startsWith(sub.path));
                    const isExpanded = expandedMenus[item.label];
                    return (
                      <div key={item.label} style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                        <SidebarTab
                          active={false}
                          onClick={() => toggleMenu(item.label)}
                          style={{ color: isActive ? '#ffffff' : undefined }}
                        >
                          <span>{item.icon}</span>
                          <span style={{ flex: 1 }}>{item.label}</span>
                          {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        </SidebarTab>

                        {isExpanded && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', paddingLeft: '1.5rem' }}>
                            {item.subItems.map(sub => (
                              <SidebarTab
                                key={sub.path}
                                active={currentPath.startsWith(sub.path)}
                                onClick={() => handleAssetClassClick(sub.path)}
                                style={{ padding: '0.5rem 1rem', fontSize: '0.8rem' }}
                              >
                                <span>{sub.icon}</span>
                                {sub.label}
                              </SidebarTab>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  }

                  return (
                    <SidebarTab
                      key={item.path}
                      active={item.path ? currentPath.startsWith(item.path) : false}
                      onClick={() => item.path && handleAssetClassClick(item.path)}
                    >
                      <span>{item.icon}</span>
                      {item.label}
                    </SidebarTab>
                  );
                })}
              </SidebarNav>
            </>
          )}
        </Sidebar>

        <ContentArea>
          <ContentInner>
            <Routes>
          {/* Default redirect to equity */}
          <Route index element={<Navigate to="equity" replace />} />

          {/* Equity */}
          <Route path="equity" element={<EquityTicketScreen />} />

          {/* Fixed Income */}
          <Route path="fixed-income" element={<FixedIncomeTicketScreen />} />

          {/* FX */}
          <Route path="fx" element={<FXTicketScreen />} />
          <Route path="fx/spot" element={<FXTicketScreen />} />
          <Route path="fx/forward" element={<FXTicketScreen />} />
          <Route path="fx/swap" element={<FXTicketScreen />} />
          <Route path="fx/ndf" element={<FXTicketScreen />} />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="equity" replace />} />
        </Routes>
          </ContentInner>
        </ContentArea>
      </PageLayout>
    </Container>
  );
};

export default RetailTradeEntry;
